import type { CreateItemResponse, Device, Item, ItemKind } from '@slipway/shared';

export interface Session {
  accessToken: string;
  refreshToken: string;
  accountId: string;
  deviceId: string;
}

const STORAGE_KEY = 'slipway.session';

export function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

export function saveSession(s: Session): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
}

export function clearSession(): void {
  localStorage.removeItem(STORAGE_KEY);
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

export class Api {
  private refreshing: Promise<void> | null = null;

  constructor(
    public baseUrl: string,
    private session: Session | null = null,
    private onSession: (s: Session | null) => void = () => {},
  ) {}

  setSession(s: Session | null): void {
    this.session = s;
    if (s) saveSession(s);
    else clearSession();
    this.onSession(s);
  }

  getSession(): Session | null {
    return this.session;
  }

  /**
   * Single retry on 401: refresh the access token, then replay. Concurrent
   * callers share one refresh so a burst of requests after a sleep does not
   * fire N rotations and invalidate each other.
   */
  private async request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
    const headers = new Headers(init.headers);
    if (this.session) headers.set('Authorization', `Bearer ${this.session.accessToken}`);
    if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');

    const res = await fetch(`${this.baseUrl}${path}`, { ...init, headers });

    if (res.status === 401 && retry && this.session?.refreshToken) {
      try {
        await this.refresh();
      } catch {
        this.setSession(null);
        throw new ApiError(401, 'unauthorized', 'Session expired. Pair this browser again.');
      }
      return this.request<T>(path, init, false);
    }

    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
      throw new ApiError(res.status, body.error ?? 'bad_request', body.message ?? res.statusText);
    }

    return (await res.json()) as T;
  }

  private async refresh(): Promise<void> {
    if (this.refreshing) return this.refreshing;
    const token = this.session?.refreshToken;
    if (!token) throw new Error('no refresh token');

    this.refreshing = (async () => {
      const res = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: token }),
      });
      if (!res.ok) throw new Error('refresh failed');
      const next = (await res.json()) as { accessToken: string; refreshToken: string };
      this.setSession({ ...this.session!, ...next });
    })().finally(() => {
      this.refreshing = null;
    });

    return this.refreshing;
  }

  /* ------------------------------------------------------------ pairing */

  async startPairing(deviceName: string, deviceKind: 'web' | 'extension' = 'web') {
    return this.request<{ code: string; expiresAt: number }>('/auth/qr/start', {
      method: 'POST',
      body: JSON.stringify({ deviceName, deviceKind }),
    });
  }

  async claimPairing(code: string) {
    return this.request<
      | { pending: true }
      | {
          account: { id: string };
          device: { id: string };
          accessToken: string;
          refreshToken: string;
        }
    >('/auth/qr/claim', { method: 'POST', body: JSON.stringify({ code }) });
  }

  /* -------------------------------------------------------------- items */

  async sync(since: string | null) {
    const q = since ? `?since=${encodeURIComponent(since)}` : '';
    return this.request<{ items: Item[]; cursor: string | null }>(`/items${q}`);
  }

  async recent(limit = 50) {
    return this.request<{ items: Item[] }>(`/items/recent?limit=${limit}`);
  }

  async createText(id: string, text: string, kind: ItemKind = 'text') {
    return this.request<CreateItemResponse>('/items', {
      method: 'POST',
      body: JSON.stringify({ id, kind, text }),
    });
  }

  async createBlob(meta: {
    id: string;
    kind: ItemKind;
    filename: string;
    mime: string;
    size: number;
    sha256: string;
  }) {
    return this.request<CreateItemResponse>('/items', {
      method: 'POST',
      body: JSON.stringify(meta),
    });
  }

  async completeBlob(id: string) {
    return this.request<{ item: Item }>(`/items/${id}/complete`, { method: 'POST' });
  }

  async downloadUrl(id: string) {
    return this.request<{ url: string }>(`/items/${id}/url`);
  }

  async receipt(id: string) {
    return this.request<{ ok: true }>(`/items/${id}/receipt`, { method: 'POST' });
  }

  async devices() {
    return this.request<{ devices: Device[]; currentDeviceId: string }>('/devices');
  }

  async revokeDevice(id: string) {
    return this.request<{ ok: true }>(`/devices/${id}`, { method: 'DELETE' });
  }

  /**
   * Uploads bytes to the presigned target. Proxy-mode URLs point back at the
   * Worker and need our bearer token; true presigned R2 URLs must NOT carry it
   * (the signature covers the headers, and an extra Authorization breaks it).
   */
  async uploadBytes(target: { url: string; headers: Record<string, string> }, body: Blob) {
    const headers = new Headers(target.headers);
    const isProxy = target.url.startsWith(this.baseUrl);
    if (isProxy && this.session) {
      headers.set('Authorization', `Bearer ${this.session.accessToken}`);
    }
    const res = await fetch(target.url, { method: 'PUT', headers, body });
    if (!res.ok) throw new ApiError(res.status, 'upload_failed', `Upload failed (${res.status}).`);
  }

  websocketUrl(): string {
    const base = this.baseUrl.replace(/^http/, 'ws');
    return `${base}/hub?token=${encodeURIComponent(this.session?.accessToken ?? '')}`;
  }
}

export async function sha256Hex(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}
